import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  PlusIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
  PhotoIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import config from '../config/api';

const API_URL = config.TASKS_API_URL;

const LandingPages = () => {
  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);

  // Editor state
  const [editorContent, setEditorContent] = useState({});
  const [uploading, setUploading] = useState({});

  // Selected items
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    template_id: null,
    meta_title: '',
    meta_description: '',
    content_json: {}
  });

  const [domainData, setDomainData] = useState({ custom_domain: '' });
  const [aiData, setAiData] = useState({
    business_type: '',
    business_name: '',
    business_description: '',
    target_audience: ''
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Generate slug from name with random suffix
  const generateSlug = (name) => {
    const randomStr = Math.random().toString(36).substring(2, 9);
    const baseSlug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'd')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    return `${baseSlug}-${randomStr}`;
  };

  useEffect(() => {
    fetchPages();
    fetchTemplates();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/landing`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      setError('Không thể tải danh sách landing pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/landing/templates`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Vui lòng nhập tên trang');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/landing`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug || undefined,
          template_id: selectedTemplate?.id || null
        })
      });

      if (response.ok) {
        const newPage = await response.json();
        setPages([newPage, ...pages]);
        setShowCreateModal(false);
        setFormData({ name: '', slug: '', template_id: null, meta_title: '', meta_description: '', content_json: {} });
        setSelectedTemplate(null);
        setSuccess('Tạo landing page thành công!');
        setTimeout(() => setSuccess(null), 3000);
        // Auto open editor after create
        setTimeout(() => openEditorModal(newPage), 500);
      } else {
        const err = await response.json();
        setError(err.detail || 'Không thể tạo landing page');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPage) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          meta_title: formData.meta_title,
          meta_description: formData.meta_description,
          content_json: formData.content_json,
          status: formData.status
        })
      });

      if (response.ok) {
        const updatedPage = await response.json();
        setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
        setShowEditModal(false);
        setSuccess('Cập nhật thành công!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Không thể cập nhật');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPage) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setPages(pages.filter(p => p.id !== selectedPage.id));
        setShowDeleteModal(false);
        setSelectedPage(null);
        setSuccess('Đã xóa landing page');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError('Lỗi khi xóa');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (page, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/landing/${page.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updatedPage = await response.json();
        setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
        setSuccess(newStatus === 'published' ? 'Đã xuất bản!' : 'Đã chuyển về nháp');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError('Lỗi khi thay đổi trạng thái');
    }
  };

  const handleBindDomain = async () => {
    if (!selectedPage || !domainData.custom_domain.trim()) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}/bind-domain`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(domainData)
      });

      if (response.ok) {
        const result = await response.json();
        setPages(pages.map(p => p.id === result.page.id ? result.page : p));
        setShowDomainModal(false);
        setSuccess('Đã bind domain! Vui lòng cấu hình DNS theo hướng dẫn.');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Không thể bind domain');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async (page) => {
    try {
      const response = await fetch(`${API_URL}/landing/${page.id}/verify-domain`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        setPages(pages.map(p => p.id === page.id ? { ...p, domain_verified: result.verified } : p));
        setSuccess('Domain đã được xác minh!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError('Không thể xác minh domain');
    }
  };

  const handleAIGenerate = async () => {
    if (!selectedPage) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}/ai/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(aiData)
      });

      if (response.ok) {
        const result = await response.json();
        setPages(pages.map(p => p.id === result.page.id ? result.page : p));
        setShowAIModal(false);
        setSuccess('AI đã tạo nội dung thành công!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'AI không thể tạo nội dung');
      }
    } catch (error) {
      setError('Lỗi kết nối AI');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (page) => {
    setSelectedPage(page);
    setFormData({
      name: page.name,
      slug: page.slug,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      content_json: page.content_json || {},
      status: page.status
    });
    setShowEditModal(true);
  };

  const openDomainModal = (page) => {
    setSelectedPage(page);
    setDomainData({ custom_domain: page.custom_domain || '' });
    setShowDomainModal(true);
  };

  const openAIModal = (page) => {
    setSelectedPage(page);
    setAiData({
      business_type: '',
      business_name: page.name,
      business_description: '',
      target_audience: ''
    });
    setShowAIModal(true);
  };

  const openEditorModal = (page) => {
    setSelectedPage(page);
    // Get template default content or existing content
    const template = templates.find(t => t.id === page.template_id);
    const content = page.content_json || template?.default_content || {};
    setEditorContent(content);
    setFormData({
      name: page.name,
      slug: page.slug,
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      content_json: content,
      status: page.status
    });
    setShowEditorModal(true);
  };

  const handleEditorSave = async () => {
    if (!selectedPage) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          meta_title: formData.meta_title,
          meta_description: formData.meta_description,
          content_json: editorContent
        })
      });

      if (response.ok) {
        const updatedPage = await response.json();
        setPages(pages.map(p => p.id === updatedPage.id ? updatedPage : p));
        setSuccess('Đã lưu thay đổi!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Không thể lưu');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
    } finally {
      setSaving(false);
    }
  };

  const updateEditorField = (path, value) => {
    const keys = path.split('.');
    setEditorContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      let obj = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newContent;
    });
  };

  const addArrayItem = (path, defaultItem) => {
    const keys = path.split('.');
    setEditorContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      let obj = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      const lastKey = keys[keys.length - 1];
      if (!obj[lastKey]) obj[lastKey] = [];
      obj[lastKey].push(defaultItem);
      return newContent;
    });
  };

  const removeArrayItem = (path, index) => {
    const keys = path.split('.');
    setEditorContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      let obj = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]].splice(index, 1);
      return newContent;
    });
  };

  const getNestedValue = (obj, path) => {
    const keys = path.split('.');
    let value = obj;
    for (const key of keys) {
      if (value === undefined || value === null) return '';
      value = value[key];
    }
    return value || '';
  };

  const handleImageUpload = async (imageType, file) => {
    if (!selectedPage || !file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(prev => ({ ...prev, [imageType]: true }));
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}/upload-image?image_type=${imageType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        // Update editorContent with new image URL
        setEditorContent(prev => ({
          ...prev,
          images: {
            ...prev.images,
            [imageType]: result.url
          }
        }));
        setSuccess(`Tải ảnh ${imageType} thành công!`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const err = await response.json();
        setError(err.detail || 'Không thể tải ảnh lên');
      }
    } catch (error) {
      setError('Lỗi khi tải ảnh');
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  const handleImageDelete = async (imageType) => {
    if (!selectedPage) return;

    try {
      setUploading(prev => ({ ...prev, [imageType]: true }));
      const response = await fetch(`${API_URL}/landing/${selectedPage.id}/delete-image/${imageType}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setEditorContent(prev => {
          const newImages = { ...prev.images };
          delete newImages[imageType];
          return { ...prev, images: newImages };
        });
        setSuccess('Đã xóa ảnh');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (error) {
      setError('Lỗi khi xóa ảnh');
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  // Generate preview HTML from editorContent
  const generatePreviewHtml = () => {
    const content = editorContent;
    const template = templates.find(t => t.id === selectedPage?.template_id);
    const images = content?.images || {};

    // Simple template based on category
    const category = template?.category || 'business';
    const colors = {
      business: { primary: '#2563eb', secondary: '#1e40af', bg: '#f8fafc' },
      restaurant: { primary: '#d97706', secondary: '#92400e', bg: '#fffbeb' },
      beauty: { primary: '#ec4899', secondary: '#be185d', bg: '#fdf2f8' }
    };
    const c = colors[category] || colors.business;

    return `
<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formData.meta_title || selectedPage?.name || 'Preview'}</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; }
    .hero-gradient { background: linear-gradient(135deg, ${c.primary} 0%, ${c.secondary} 100%); }
  </style>
</head>
<body class="bg-gray-50">
  <!-- Hero Section -->
  <section class="hero-gradient text-white py-16 px-4">
    <div class="max-w-4xl mx-auto text-center">
      ${images.logo ? `<img src="https://fe-api.smartera.uk${images.logo}" alt="Logo" class="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-4 border-white/30">` : ''}
      <h1 class="text-3xl md:text-4xl font-bold mb-3">${content?.hero?.title || 'Tiêu đề chính'}</h1>
      <p class="text-lg opacity-90 mb-6">${content?.hero?.subtitle || 'Phụ đề mô tả'}</p>
      ${content?.hero?.cta_text ? `<a href="${content?.hero?.cta_link || '#'}" class="inline-block bg-white text-gray-800 px-6 py-2 rounded-full font-semibold hover:bg-gray-100 transition">${content.hero.cta_text}</a>` : ''}
    </div>
  </section>

  ${images.banner1 ? `
  <!-- Banner 1 -->
  <div class="w-full">
    <img src="https://fe-api.smartera.uk${images.banner1}" alt="Banner" class="w-full h-48 object-cover">
  </div>
  ` : ''}

  <!-- Features/Services/Menu Section -->
  ${content?.features || content?.services || content?.menu ? `
  <section class="py-12 px-4">
    <div class="max-w-4xl mx-auto">
      <h2 class="text-2xl font-bold text-center mb-8 text-gray-800">${content?.features?.title || content?.services?.title || content?.menu?.title || 'Dịch vụ'}</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        ${(content?.features?.items || content?.services?.items || content?.menu?.items || []).map(item => `
          <div class="bg-white p-4 rounded-xl shadow-sm border">
            ${item.image ? `<img src="${item.image}" alt="${item.name || item.title}" class="w-full h-32 object-cover rounded-lg mb-3">` : ''}
            <div class="text-2xl mb-2">${item.icon || ''}</div>
            <h3 class="font-semibold text-gray-800">${item.name || item.title || ''}</h3>
            <p class="text-sm text-gray-600 mt-1">${item.description || ''}</p>
            ${item.price ? `<p class="text-sm font-bold mt-2" style="color: ${c.primary}">${item.price}</p>` : ''}
          </div>
        `).join('')}
      </div>
    </div>
  </section>
  ` : ''}

  ${images.banner2 ? `
  <!-- Banner 2 -->
  <div class="w-full">
    <img src="https://fe-api.smartera.uk${images.banner2}" alt="Banner" class="w-full h-48 object-cover">
  </div>
  ` : ''}

  <!-- Contact Section -->
  ${content?.contact ? `
  <section class="py-12 px-4" style="background: ${c.primary}">
    <div class="max-w-4xl mx-auto text-center text-white">
      <h2 class="text-2xl font-bold mb-6">${content.contact.title || 'Liên hệ'}</h2>
      ${content.contact.phone ? `<p class="text-lg mb-2">📞 ${content.contact.phone}</p>` : ''}
      ${content.contact.email ? `<p class="mb-2">✉️ ${content.contact.email}</p>` : ''}
      ${content.contact.address ? `<p class="opacity-90">📍 ${content.contact.address}</p>` : ''}
      ${content.contact.hours ? `<p class="mt-2 opacity-80">🕐 ${content.contact.hours}</p>` : ''}
    </div>
  </section>
  ` : ''}

  <!-- Footer -->
  <footer class="bg-gray-800 text-white py-4 text-center text-sm">
    <p>${content?.footer?.copyright || '© 2024 All rights reserved.'}</p>
  </footer>
</body>
</html>`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-slate-100 text-slate-700',
      'published': 'bg-emerald-100 text-emerald-700',
      'archived': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'draft': 'Nháp',
      'published': 'Đã xuất bản',
      'archived': 'Đã lưu trữ'
    };
    return statusMap[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'published':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'archived':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const filteredPages = pages.filter(page => {
    const matchesStatus = filterStatus === 'ALL' || page.status === filterStatus;
    const matchesSearch = page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         page.slug.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: pages.length,
    published: pages.filter(p => p.status === 'published').length,
    draft: pages.filter(p => p.status === 'draft').length,
    withDomain: pages.filter(p => p.custom_domain).length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-primary-200 rounded-full"></div>
          <div className="w-20 h-20 border-4 border-primary-500 rounded-full animate-spin absolute top-0 left-0 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Landing Pages</h1>
          <p className="text-slate-500 mt-1">Tạo và quản lý landing page cho doanh nghiệp của bạn</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Tạo Landing Page
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckIcon className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DocumentDuplicateIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">Tổng số trang</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.published}</p>
              <p className="text-sm text-slate-500">Đã xuất bản</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.draft}</p>
              <p className="text-sm text-slate-500">Bản nháp</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <GlobeAltIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.withDomain}</p>
              <p className="text-sm text-slate-500">Có domain riêng</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm kiếm theo tên hoặc slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">Tất cả trạng thái</option>
              <option value="draft">Nháp</option>
              <option value="published">Đã xuất bản</option>
              <option value="archived">Đã lưu trữ</option>
            </select>
          </div>
          <button
            onClick={fetchPages}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5" />
            Làm mới
          </button>
        </div>
      </div>

      {/* Pages List */}
      {filteredPages.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-slate-100 text-center">
          <DocumentDuplicateIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Chưa có landing page nào</h3>
          <p className="text-slate-500 mb-4">Bắt đầu tạo landing page đầu tiên của bạn</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            Tạo Landing Page
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredPages.map((page) => (
            <div key={page.id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-800">{page.name}</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(page.status)}`}>
                      {getStatusIcon(page.status)}
                      {getStatusText(page.status)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <LinkIcon className="w-4 h-4" />
                      /{page.slug}
                    </span>
                    {page.custom_domain && (
                      <span className="flex items-center gap-1">
                        <GlobeAltIcon className="w-4 h-4" />
                        {page.custom_domain}
                        {page.domain_verified ? (
                          <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
                        )}
                      </span>
                    )}
                    {page.template_id && (
                      <span className="text-purple-600">
                        Template #{page.template_id}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Cập nhật: {dayjs(page.updated_at).format('DD/MM/YYYY HH:mm')}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {/* AI Generate */}
                  <button
                    onClick={() => openAIModal(page)}
                    className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="AI tạo nội dung"
                  >
                    <SparklesIcon className="w-5 h-5" />
                  </button>

                  {/* Preview */}
                  <a
                    href={`https://fe-api.smartera.uk/landing/view/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Xem trước"
                  >
                    <EyeIcon className="w-5 h-5" />
                  </a>

                  {/* Edit - opens WYSIWYG editor */}
                  <button
                    onClick={() => openEditorModal(page)}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Chỉnh sửa"
                  >
                    <PencilSquareIcon className="w-5 h-5" />
                  </button>

                  {/* Domain */}
                  <button
                    onClick={() => openDomainModal(page)}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Cấu hình domain"
                  >
                    <GlobeAltIcon className="w-5 h-5" />
                  </button>

                  {/* Publish/Unpublish */}
                  {page.status === 'draft' ? (
                    <button
                      onClick={() => handlePublish(page, 'published')}
                      className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      Xuất bản
                    </button>
                  ) : page.status === 'published' ? (
                    <button
                      onClick={() => handlePublish(page, 'draft')}
                      className="px-3 py-1.5 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors"
                    >
                      Gỡ xuất bản
                    </button>
                  ) : null}

                  {/* Delete */}
                  <button
                    onClick={() => {
                      setSelectedPage(page);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Xóa"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Tạo Landing Page mới</h2>
            </div>
            <div className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tên trang <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData,
                      name: name,
                      slug: name ? generateSlug(name) : ''
                    });
                  }}
                  placeholder="VD: Spa Luxury Beauty"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Slug (auto-generated, readonly) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Slug (URL)
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-200 rounded-l-lg text-slate-500 text-sm">
                    /landing/view/
                  </span>
                  <input
                    type="text"
                    value={formData.slug}
                    readOnly
                    placeholder="tu-dong-tao-tu-ten"
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-r-lg bg-slate-50 text-slate-600"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Tự động tạo từ tên trang với mã ngẫu nhiên</p>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Chọn Template
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center text-white font-bold">
                          {template.name.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-800">{template.name}</h4>
                          <p className="text-sm text-slate-500">{template.description}</p>
                          <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                            {template.category}
                          </span>
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <CheckCircleIcon className="w-6 h-6 text-primary-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedTemplate(null);
                  setFormData({ name: '', slug: '', template_id: null, meta_title: '', meta_description: '', content_json: {} });
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang tạo...' : 'Tạo trang'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Chỉnh sửa: {selectedPage.name}</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tên trang</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Meta Title (SEO)</label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  placeholder="Tiêu đề hiển thị trên Google"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Meta Description (SEO)</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  placeholder="Mô tả ngắn hiển thị trên Google"
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Content JSON Editor */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nội dung (JSON)</label>
                <textarea
                  value={JSON.stringify(formData.content_json, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setFormData({ ...formData, content_json: parsed });
                    } catch (err) {
                      // Invalid JSON, keep as is
                    }
                  }}
                  rows={12}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleUpdate}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Domain Modal */}
      {showDomainModal && selectedPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Cấu hình Domain</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Custom Domain</label>
                <input
                  type="text"
                  value={domainData.custom_domain}
                  onChange={(e) => setDomainData({ custom_domain: e.target.value })}
                  placeholder="example.com"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {selectedPage.custom_domain && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-medium text-amber-800 mb-2">Hướng dẫn cấu hình DNS</h4>
                  <p className="text-sm text-amber-700 mb-2">Thêm các record sau vào DNS của domain:</p>
                  <div className="bg-white rounded p-2 text-sm font-mono">
                    <p>CNAME: {selectedPage.custom_domain} → landing.smartera.uk</p>
                    <p>TXT: _smartera.{selectedPage.custom_domain} → landing-page-{selectedPage.id}</p>
                  </div>
                  {!selectedPage.domain_verified && (
                    <button
                      onClick={() => handleVerifyDomain(selectedPage)}
                      className="mt-3 px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
                    >
                      Xác minh Domain
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDomainModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Đóng
              </button>
              <button
                onClick={handleBindDomain}
                disabled={saving || !domainData.custom_domain.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang lưu...' : 'Bind Domain'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Generate Modal */}
      {showAIModal && selectedPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-slate-800">AI Tạo Nội Dung</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Loại hình kinh doanh</label>
                <select
                  value={aiData.business_type}
                  onChange={(e) => setAiData({ ...aiData, business_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Chọn loại hình</option>
                  <option value="restaurant">Nhà hàng / Quán ăn</option>
                  <option value="spa">Spa / Làm đẹp</option>
                  <option value="gym">Gym / Fitness</option>
                  <option value="education">Giáo dục / Đào tạo</option>
                  <option value="ecommerce">Thương mại điện tử</option>
                  <option value="service">Dịch vụ chuyên nghiệp</option>
                  <option value="healthcare">Y tế / Sức khỏe</option>
                  <option value="realestate">Bất động sản</option>
                  <option value="other">Khác</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tên doanh nghiệp</label>
                <input
                  type="text"
                  value={aiData.business_name}
                  onChange={(e) => setAiData({ ...aiData, business_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Mô tả ngắn</label>
                <textarea
                  value={aiData.business_description}
                  onChange={(e) => setAiData({ ...aiData, business_description: e.target.value })}
                  placeholder="Mô tả về doanh nghiệp, sản phẩm/dịch vụ chính..."
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Đối tượng khách hàng</label>
                <input
                  type="text"
                  value={aiData.target_audience}
                  onChange={(e) => setAiData({ ...aiData, target_audience: e.target.value })}
                  placeholder="VD: Phụ nữ 25-45 tuổi, yêu thích làm đẹp"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAIModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleAIGenerate}
                disabled={saving || !aiData.business_type || !aiData.business_name}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    Đang tạo...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-5 h-5" />
                    Tạo nội dung
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">Xác nhận xóa</h2>
              </div>
              <p className="text-slate-600">
                Bạn có chắc muốn xóa landing page <strong>"{selectedPage.name}"</strong>?
                Hành động này không thể hoàn tác.
              </p>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedPage(null);
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Đang xóa...' : 'Xóa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WYSIWYG Editor Modal */}
      {showEditorModal && selectedPage && (
        <div className="fixed inset-0 bg-black/80 z-50">
          <div className="w-full h-full flex flex-col">
            {/* Editor Header */}
            <div className="p-3 border-b border-slate-700 flex items-center justify-between bg-slate-800 shrink-0">
              <div className="flex items-center gap-3">
                <PencilSquareIcon className="w-5 h-5 text-cyan-400" />
                <div>
                  <h2 className="text-base font-bold text-white">WYSIWYG Editor: {selectedPage.name}</h2>
                  <p className="text-xs text-slate-400">Chỉnh sửa bên trái - Xem kết quả bên phải</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`https://fe-api.smartera.uk/landing/view/${selectedPage.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <GlobeAltIcon className="w-4 h-4" />
                  Mở trang thật
                </a>
                <button
                  onClick={handleEditorSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckIcon className="w-4 h-4" />}
                  {saving ? 'Đang lưu...' : 'Lưu'}
                </button>
                <button
                  onClick={() => setShowEditorModal(false)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* WYSIWYG Content - 2 Columns */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left Panel - Editor Form */}
              <div className="w-[420px] bg-slate-900 border-r border-slate-700 overflow-y-auto">
                <div className="p-4 space-y-4">
                {/* Image Upload Section */}
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <PhotoIcon className="w-4 h-4 text-purple-400" />
                    Hình ảnh
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Logo */}
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Logo</label>
                      <div className="relative">
                        {editorContent?.images?.logo ? (
                          <div className="relative group">
                            <img
                              src={editorContent.images.logo.startsWith('http') ? editorContent.images.logo : `https://fe-api.smartera.uk${editorContent.images.logo}`}
                              alt="Logo"
                              className="w-full h-20 object-cover rounded-lg border border-slate-600"
                            />
                            <button
                              onClick={() => handleImageDelete('logo')}
                              disabled={uploading.logo}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-slate-700 transition-colors">
                            <CloudArrowUpIcon className="w-6 h-6 text-slate-500" />
                            <span className="text-xs text-slate-500 mt-1">Logo</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleImageUpload('logo', e.target.files[0])}
                              disabled={uploading.logo}
                            />
                          </label>
                        )}
                        {uploading.logo && (
                          <div className="absolute inset-0 bg-slate-800/80 flex items-center justify-center rounded-lg">
                            <ArrowPathIcon className="w-5 h-5 animate-spin text-purple-400" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Banner 1 */}
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Banner 1</label>
                      <div className="relative">
                        {editorContent?.images?.banner1 ? (
                          <div className="relative group">
                            <img
                              src={editorContent.images.banner1.startsWith('http') ? editorContent.images.banner1 : `https://fe-api.smartera.uk${editorContent.images.banner1}`}
                              alt="Banner 1"
                              className="w-full h-20 object-cover rounded-lg border border-slate-600"
                            />
                            <button
                              onClick={() => handleImageDelete('banner1')}
                              disabled={uploading.banner1}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-slate-700 transition-colors">
                            <CloudArrowUpIcon className="w-6 h-6 text-slate-500" />
                            <span className="text-xs text-slate-500 mt-1">Banner</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleImageUpload('banner1', e.target.files[0])}
                              disabled={uploading.banner1}
                            />
                          </label>
                        )}
                        {uploading.banner1 && (
                          <div className="absolute inset-0 bg-slate-800/80 flex items-center justify-center rounded-lg">
                            <ArrowPathIcon className="w-5 h-5 animate-spin text-purple-400" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Banner 2 */}
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Banner 2</label>
                      <div className="relative">
                        {editorContent?.images?.banner2 ? (
                          <div className="relative group">
                            <img
                              src={editorContent.images.banner2.startsWith('http') ? editorContent.images.banner2 : `https://fe-api.smartera.uk${editorContent.images.banner2}`}
                              alt="Banner 2"
                              className="w-full h-20 object-cover rounded-lg border border-slate-600"
                            />
                            <button
                              onClick={() => handleImageDelete('banner2')}
                              disabled={uploading.banner2}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-slate-700 transition-colors">
                            <CloudArrowUpIcon className="w-6 h-6 text-slate-500" />
                            <span className="text-xs text-slate-500 mt-1">Banner</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleImageUpload('banner2', e.target.files[0])}
                              disabled={uploading.banner2}
                            />
                          </label>
                        )}
                        {uploading.banner2 && (
                          <div className="absolute inset-0 bg-slate-800/80 flex items-center justify-center rounded-lg">
                            <ArrowPathIcon className="w-5 h-5 animate-spin text-purple-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO Settings */}
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <MagnifyingGlassIcon className="w-4 h-4 text-blue-400" />
                    SEO
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Meta Title</label>
                      <input
                        type="text"
                        value={formData.meta_title}
                        onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                        placeholder="Tiêu đề Google"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Meta Description</label>
                      <input
                        type="text"
                        value={formData.meta_description}
                        onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                        placeholder="Mô tả ngắn"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Hero Section */}
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded flex items-center justify-center text-xs font-bold">1</span>
                    Hero Section
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Tiêu đề chính</label>
                      <input
                        type="text"
                        value={getNestedValue(editorContent, 'hero.title')}
                        onChange={(e) => updateEditorField('hero.title', e.target.value)}
                        placeholder="Chào mừng đến..."
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Phụ đề</label>
                      <input
                        type="text"
                        value={getNestedValue(editorContent, 'hero.subtitle')}
                        onChange={(e) => updateEditorField('hero.subtitle', e.target.value)}
                        placeholder="Giải pháp tốt nhất"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Nút CTA</label>
                        <input
                          type="text"
                          value={getNestedValue(editorContent, 'hero.cta_text')}
                          onChange={(e) => updateEditorField('hero.cta_text', e.target.value)}
                          placeholder="Liên hệ ngay"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Link CTA</label>
                        <input
                          type="text"
                          value={getNestedValue(editorContent, 'hero.cta_link')}
                          onChange={(e) => updateEditorField('hero.cta_link', e.target.value)}
                          placeholder="#contact"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    {editorContent?.hero?.background_image !== undefined && (
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Ảnh nền (URL)</label>
                        <input
                          type="text"
                          value={getNestedValue(editorContent, 'hero.background_image')}
                          onChange={(e) => updateEditorField('hero.background_image', e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Features/Services Section */}
                {(editorContent?.features || editorContent?.services || editorContent?.menu) && (
                  <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                        <span className="w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded flex items-center justify-center text-xs font-bold">2</span>
                        {editorContent?.features ? 'Dịch vụ' : editorContent?.menu ? 'Thực đơn' : 'Dịch vụ'}
                      </h3>
                      <button
                        onClick={() => {
                          const sectionKey = editorContent?.features ? 'features' : editorContent?.menu ? 'menu' : 'services';
                          const defaultItem = editorContent?.menu
                            ? { name: '', description: '', price: '', image: '' }
                            : { title: '', description: '', icon: '⭐' };
                          addArrayItem(`${sectionKey}.items`, defaultItem);
                        }}
                        className="flex items-center gap-1 px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
                      >
                        <PlusIcon className="w-3 h-3" />
                        Thêm
                      </button>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-medium text-slate-400 mb-1">Tiêu đề section</label>
                      <input
                        type="text"
                        value={getNestedValue(editorContent, editorContent?.features ? 'features.title' : editorContent?.menu ? 'menu.title' : 'services.title')}
                        onChange={(e) => updateEditorField(editorContent?.features ? 'features.title' : editorContent?.menu ? 'menu.title' : 'services.title', e.target.value)}
                        placeholder="Dịch vụ của chúng tôi"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {(editorContent?.features?.items || editorContent?.services?.items || editorContent?.menu?.items || []).map((item, index) => {
                        const sectionKey = editorContent?.features ? 'features' : editorContent?.menu ? 'menu' : 'services';
                        return (
                          <div key={index} className="bg-slate-700/50 rounded-lg p-3 relative group">
                            <button
                              onClick={() => removeArrayItem(`${sectionKey}.items`, index)}
                              className="absolute top-2 right-2 p-1 text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                            <div className="grid grid-cols-2 gap-2">
                              {editorContent?.menu ? (
                                <>
                                  <div>
                                    <input
                                      type="text"
                                      value={item.name || ''}
                                      onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.name`, e.target.value)}
                                      placeholder="Tên món"
                                      className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                    />
                                  </div>
                                  <div>
                                    <input
                                      type="text"
                                      value={item.price || ''}
                                      onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.price`, e.target.value)}
                                      placeholder="Giá"
                                      className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <input
                                      type="text"
                                      value={item.description || ''}
                                      onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.description`, e.target.value)}
                                      placeholder="Mô tả"
                                      className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                    />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <input
                                      type="text"
                                      value={item.title || item.name || ''}
                                      onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.${item.title !== undefined ? 'title' : 'name'}`, e.target.value)}
                                      placeholder="Tiêu đề"
                                      className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                    />
                                  </div>
                                  <div>
                                    <input
                                      type="text"
                                      value={item.icon || ''}
                                      onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.icon`, e.target.value)}
                                      placeholder="Icon ⭐"
                                      className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <input
                                      type="text"
                                      value={item.description || ''}
                                      onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.description`, e.target.value)}
                                      placeholder="Mô tả"
                                      className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                    />
                                  </div>
                                  {item.price !== undefined && (
                                    <div className="col-span-2">
                                      <input
                                        type="text"
                                        value={item.price || ''}
                                        onChange={(e) => updateEditorField(`${sectionKey}.items.${index}.price`, e.target.value)}
                                        placeholder="Giá"
                                        className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs placeholder-slate-400"
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {(editorContent?.features?.items?.length === 0 || editorContent?.services?.items?.length === 0 || editorContent?.menu?.items?.length === 0) && (
                        <div className="text-center py-4 text-slate-500 text-xs">
                          <p>Chưa có mục nào</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Section */}
                {editorContent?.contact && (
                  <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-purple-500/20 text-purple-400 rounded flex items-center justify-center text-xs font-bold">3</span>
                      Liên hệ
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Tiêu đề</label>
                        <input
                          type="text"
                          value={getNestedValue(editorContent, 'contact.title')}
                          onChange={(e) => updateEditorField('contact.title', e.target.value)}
                          placeholder="Liên hệ với chúng tôi"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-slate-400 mb-1">Điện thoại</label>
                          <input
                            type="text"
                            value={getNestedValue(editorContent, 'contact.phone')}
                            onChange={(e) => updateEditorField('contact.phone', e.target.value)}
                            placeholder="0123 456 789"
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-400 mb-1">Email</label>
                          <input
                            type="email"
                            value={getNestedValue(editorContent, 'contact.email')}
                            onChange={(e) => updateEditorField('contact.email', e.target.value)}
                            placeholder="email@example.com"
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-400 mb-1">Địa chỉ</label>
                        <input
                          type="text"
                          value={getNestedValue(editorContent, 'contact.address')}
                          onChange={(e) => updateEditorField('contact.address', e.target.value)}
                          placeholder="123 Đường ABC, TP.HCM"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      {editorContent?.contact?.hours !== undefined && (
                        <div>
                          <label className="block text-xs font-medium text-slate-400 mb-1">Giờ mở cửa</label>
                          <input
                            type="text"
                            value={getNestedValue(editorContent, 'contact.hours')}
                            onChange={(e) => updateEditorField('contact.hours', e.target.value)}
                            placeholder="8:00 - 22:00"
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer Section */}
                {editorContent?.footer && (
                  <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-slate-600 text-slate-300 rounded flex items-center justify-center text-xs font-bold">4</span>
                      Footer
                    </h3>
                    <div>
                      <label className="block text-xs font-medium text-slate-400 mb-1">Copyright</label>
                      <input
                        type="text"
                        value={getNestedValue(editorContent, 'footer.copyright')}
                        onChange={(e) => updateEditorField('footer.copyright', e.target.value)}
                        placeholder="© 2024 All rights reserved."
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
                </div>
              </div>

              {/* Right Panel - Live Preview */}
              <div className="flex-1 bg-slate-800 flex flex-col">
                <div className="p-3 bg-slate-700 border-b border-slate-600 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <EyeIcon className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-medium text-white">Live Preview</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const iframe = document.getElementById('preview-iframe');
                        if (iframe) iframe.src = iframe.src;
                      }}
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-600 rounded transition-colors"
                      title="Refresh preview"
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex-1 bg-white">
                  <iframe
                    id="preview-iframe"
                    srcDoc={generatePreviewHtml()}
                    className="w-full h-full border-0"
                    title="Preview"
                  />
                </div>
              </div>
            </div>

            {/* Editor Footer */}
            <div className="p-3 border-t border-slate-700 bg-slate-800 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-500">
                Template: {templates.find(t => t.id === selectedPage.template_id)?.name || 'Không có'}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowEditorModal(false)}
                  className="px-3 py-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors text-sm"
                >
                  Đóng
                </button>
                <button
                  onClick={handleEditorSave}
                  disabled={saving}
                  className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2 text-sm"
                >
                  {saving ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      Lưu
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPages;
